import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Eye, 
  MousePointer, 
  Mail,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle
} from 'lucide-react'
import { 
  getEmailLogCampaignsSequence,
  getEmailLogCampaignsBulk,
  getEmailLogCampaignSequenceLogs,
  getEmailLogCampaignBulkLogs,
  getRecipientTrackingStats
} from '../../services/api'

export default function EmailTrackingStats() {
  const [searchQuery, setSearchQuery] = useState('')
  const [view, setView] = useState('sequence')
  const [page, setPage] = useState(1)
  const [limit] = useState(25)
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set())
  const [campaignDetails, setCampaignDetails] = useState({})
  const [emailLookup, setEmailLookup] = useState('')
  const [emailStats, setEmailStats] = useState(null)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailError, setEmailError] = useState('')

  useEffect(() => {
    setPage(1)
    setExpandedCampaigns(new Set())
  }, [view, searchQuery])

  const { data, isLoading } = useQuery({
    queryKey: ['emailLogCampaigns', view, searchQuery, page, limit],
    queryFn: () => {
      const options = { page, limit }
      if (searchQuery) options.q = searchQuery
      return view === 'sequence'
        ? getEmailLogCampaignsSequence(options)
        : getEmailLogCampaignsBulk(options)
    },
    staleTime: 30000,
  })

  const campaigns = data?.items || []
  const totalCampaigns = data?.total || campaigns.length

  // Stats globales
  const totalEmails = campaigns.reduce((sum, campaign) => sum + (campaign.total || 0), 0)
  const totalOpens = campaigns.reduce((sum, campaign) => sum + (campaign.opens || 0), 0)
  const totalClicks = campaigns.reduce((sum, campaign) => sum + (campaign.clicks || 0), 0)

  const toggleCampaign = async (campaignId) => {
    const newExpanded = new Set(expandedCampaigns)
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId)
      setExpandedCampaigns(newExpanded)
      return
    }

    newExpanded.add(campaignId)
    setExpandedCampaigns(newExpanded)

    if (!campaignDetails[campaignId]) {
      try {
        const detail = view === 'sequence'
          ? await getEmailLogCampaignSequenceLogs(campaignId, { page: 1, limit: 20 })
          : await getEmailLogCampaignBulkLogs(campaignId, { page: 1, limit: 20 })
        setCampaignDetails(prev => ({ ...prev, [campaignId]: detail }))
      } catch (error) {
        console.error('Campaign detail load error:', error)
      }
    }
  }

  const handleEmailLookup = async () => {
    const email = emailLookup.trim()
    if (!email) {
      setEmailStats(null)
      setEmailError('')
      return
    }

    setEmailLoading(true)
    setEmailError('')
    try {
      const response = await getRecipientTrackingStats(email)
      setEmailStats(response?.stats || null)
    } catch (error) {
      setEmailError(error?.response?.data?.error || 'Erreur lors du chargement des stats')
      setEmailStats(null)
    } finally {
      setEmailLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes}min`
    if (hours < 24) return `Il y a ${hours}h`
    if (days < 7) return `Il y a ${days}j`
    
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)
    const h = String(date.getHours()).padStart(2, '0')
    const m = String(date.getMinutes()).padStart(2, '0')
    return `${day}/${month}/${year} ${h}:${m}`
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
        <div className="spinning" style={{ 
          width: 48, 
          height: 48, 
          border: '4px solid rgba(91, 92, 244,0.1)', 
          borderTop: '4px solid #5b5cf4', 
          borderRadius: '50%', 
          margin: '0 auto 1rem' 
        }}></div>
        Chargement des statistiques...
      </div>
    )
  }

  return (
    <div>
      {/* Statistiques */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: '#888', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Campagnes
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>
            {totalCampaigns}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: '#888', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Emails envoyés</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>
            {totalEmails}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: '#888', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Ouvertures
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>
            {totalOpens}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: '#888', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Clics
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>
            {totalClicks}
          </div>
        </div>
      </div>

      {/* Tracking par email */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.75rem', color: '#fff' }}>
          Tracking par adresse email
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
          <input
            type="email"
            value={emailLookup}
            onChange={(e) => setEmailLookup(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEmailLookup()
            }}
            placeholder="ex: contact@entreprise.com"
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              color: '#fff',
              fontSize: '0.9rem'
            }}
          />
          <button
            onClick={handleEmailLookup}
            style={{
              padding: '0.75rem 1.25rem',
              background: 'rgba(91, 92, 244,0.15)',
              border: '1px solid rgba(91, 92, 244,0.3)',
              borderRadius: '8px',
              color: '#5b5cf4',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            Rechercher
          </button>
        </div>

        {emailLoading && (
          <div style={{ color: '#888', fontSize: '0.85rem' }}>Chargement des statistiques...</div>
        )}

        {emailError && (
          <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>{emailError}</div>
        )}

        {emailStats && !emailLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Emails envoyés</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{emailStats.sent || 0}</div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Ouvertures</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{emailStats.opens || 0}</div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Clics</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{emailStats.clicks || 0}</div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Dernier envoi</div>
              <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                {formatDate(emailStats.lastSentAt)}
              </div>
            </div>
            <div>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Dernière ouverture</div>
              <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                {formatDate(emailStats.lastOpenedAt)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Vue + Recherche */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setView('sequence')}
            style={{
              padding: '0.5rem 1rem',
              background: view === 'sequence' ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${view === 'sequence' ? 'rgba(91, 92, 244,0.3)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px',
              color: view === 'sequence' ? '#5b5cf4' : '#888',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Séquences
          </button>
          <button
            onClick={() => setView('bulk')}
            style={{
              padding: '0.5rem 1rem',
              background: view === 'bulk' ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${view === 'bulk' ? 'rgba(91, 92, 244,0.3)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px',
              color: view === 'bulk' ? '#5b5cf4' : '#888',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Emailing massif
          </button>
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
        <Search
          size={20}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }}
        />
        <input
          type="text"
          placeholder="Rechercher une campagne..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '0.75rem 1rem 0.75rem 3rem',
            color: '#fff',
            fontSize: '0.875rem'
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={18} />
          </button>
        )}
        </div>
      </div>

      {/* Liste des campagnes */}
      {campaigns.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Mail size={48} style={{ color: '#888', marginBottom: '1rem' }} />
          <p style={{ color: '#888', fontSize: '1rem' }}>
            {searchQuery ? 'Aucune campagne trouvée' : 'Aucune campagne disponible'}
          </p>
        </div>
      ) : (
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden'
        }}>
          {campaigns.map((campaign) => {
            const isExpanded = expandedCampaigns.has(campaign.campaignId)
            const detail = campaignDetails[campaign.campaignId]
            const detailItems = detail?.items || []

            return (
              <div
                key={campaign.campaignId}
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                {/* Campaign Header */}
                <div
                  onClick={() => toggleCampaign(campaign.campaignId)}
                  style={{
                    padding: '1rem 1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'background 0.2s',
                    background: isExpanded ? 'rgba(91, 92, 244,0.03)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'
                  }}
                  onMouseLeave={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'transparent'
                  }}
                >
                  <div style={{ color: isExpanded ? '#5b5cf4' : '#888' }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                  <div>
                    <CheckCircle size={16} style={{ color: '#5b5cf4' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: '#5b5cf4',
                      fontSize: '0.9375rem',
                      marginBottom: '0.25rem'
                    }}>
                      {campaign.campaignName || 'Sans nom'}
                    </div>
                    <div style={{ 
                      color: '#aaa', 
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {campaign.subject || `${campaign.total} emails`}
                    </div>
                  </div>

                  {/* Stats badges */}
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: campaign.opens > 0 ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: campaign.opens > 0 ? '#5b5cf4' : '#666'
                    }}>
                      <Eye size={12} />
                      {campaign.opens || 0}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: campaign.clicks > 0 ? 'rgba(91, 92, 244,0.15)' : 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: campaign.clicks > 0 ? '#5b5cf4' : '#666'
                    }}>
                      <MousePointer size={12} />
                      {campaign.clicks || 0}
                    </div>
                  </div>

                  <div style={{ fontSize: '0.8125rem', color: '#888', minWidth: '80px', textAlign: 'right' }}>
                    {formatDate(campaign.lastSentAt)}
                  </div>
                </div>

                {/* Campaign Details */}
                {isExpanded && (
                  <div style={{
                    padding: '1rem 1.5rem 1.5rem 3.5rem',
                    background: 'rgba(0,0,0,0.2)'
                  }}>
                    {!detail ? (
                      <div style={{ color: '#888', fontSize: '0.8rem' }}>Chargement des emails...</div>
                    ) : detailItems.length === 0 ? (
                      <div style={{ color: '#888', fontSize: '0.8rem' }}>Aucun email</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {detailItems.map((item) => {
                          const opened = (item.opensCount || 0) > 0
                          const clicked = (item.clicksCount || 0) > 0

                          return (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.status === 'sent' ? '#5b5cf4' : item.status === 'failed' ? '#ef4444' : '#888' }} />
                            <span style={{ color: '#888', minWidth: '120px' }}>{formatDate(item.sentAt)}</span>
                            <span style={{ color: '#fff', flex: 1 }}>{item.recipientEmail}</span>
                            <span style={{
                              color: opened ? '#5b5cf4' : '#666',
                              fontSize: '0.75rem',
                              minWidth: '90px'
                            }}>
                              Ouvert: {opened ? 'Oui' : 'Non'}
                            </span>
                            <span style={{
                              color: clicked ? '#5b5cf4' : '#666',
                              fontSize: '0.75rem',
                              minWidth: '90px'
                            }}>
                              Clique: {clicked ? 'Oui' : 'Non'}
                            </span>
                            <span style={{ color: '#666', fontSize: '0.75rem' }}>{item.subject || '—'}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{
            padding: '0.35rem 0.75rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '6px',
            color: '#888',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          ◀
        </button>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>Page {page}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={!data?.hasMore}
          style={{
            padding: '0.35rem 0.75rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '6px',
            color: '#888',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          ▶
        </button>
      </div>
    </div>
  )
}
